import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const parseISODateUTC = (iso) => {
  const [y, m, d] = String(iso || "")
    .split("-")
    .map((part) => Number(part));

  if (!y || !m || !d) {
    return null;
  }

  return new Date(Date.UTC(y, m - 1, d));
};

const formatISODateUTC = (dateObj) => {
  const y = dateObj.getUTCFullYear();
  const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const buildContinuousDateRange = (dates) => {
  const parsed = (dates || [])
    .map(parseISODateUTC)
    .filter((item) => item instanceof Date && !Number.isNaN(item.getTime()));

  if (parsed.length === 0) {
    return [];
  }

  const oneDayMs = 24 * 60 * 60 * 1000;
  const minTime = Math.min(...parsed.map((item) => item.getTime()));
  const maxTime = Math.max(...parsed.map((item) => item.getTime()));
  const labels = [];

  for (let time = minTime; time <= maxTime; time += oneDayMs) {
    labels.push(formatISODateUTC(new Date(time)));
  }

  return labels;
};

const toSeriesMap = (points) => {
  const valuesByDate = {};

  (points || []).forEach((point) => {
    const date = String(point?.date || "").trim();
    const value = Number(point?.value || 0);

    if (!date) {
      return;
    }

    valuesByDate[date] = Number.isFinite(value) ? value : 0;
  });

  return valuesByDate;
};

const pillBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 12px",
  borderRadius: "999px",
  border: "1px solid #e5e9f2",
  background: "#fff",
  cursor: "pointer",
  userSelect: "none",
  transition: "all 160ms ease",
  fontSize: "13px",
  fontWeight: 600,
  color: "#364a63",
};

const hiddenCheckbox = {
  position: "absolute",
  opacity: 0,
  pointerEvents: "none",
  width: 0,
  height: 0,
};

const dotStyle = (color) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: color,
  boxShadow: `0 0 0 4px ${color}22`,
  flexShrink: 0,
});

const TogglePill = ({ label, color, background, checked, onChange }) => (
  <label
    style={{
      ...pillBase,
      borderColor: checked ? `${color}55` : "#e5e9f2",
      background: checked ? background : "#fff",
    }}
  >
    <input
      style={hiddenCheckbox}
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span style={dotStyle(color)} />
    <span style={{ lineHeight: 1 }}>{label}</span>
    <span
      style={{
        marginLeft: 6,
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: checked ? `2px solid ${color}` : "2px solid #e5e9f2",
        background: checked ? color : "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: 12,
        fontWeight: 900,
      }}
      aria-hidden="true"
    >
      {checked ? "v" : ""}
    </span>
  </label>
);

const StockMovementChart = ({
  series = [],
  loading = false,
  error = "",
  defaultHeading = "Daily Stock Movement",
  loadingMessage = "Loading daily stock movement...",
  emptyMessage = "No daily stock movement data available.",
  yAxisTitle = "Movement (MT)",
  chartHeight = 320,
}) => {
  const [filters, setFilters] = useState({});

  useEffect(() => {
    setFilters((previous) => {
      const next = {};

      series.forEach((item) => {
        next[item.key] = previous[item.key] || false;
      });

      return next;
    });
  }, [series]);

  const isAnyChecked = useMemo(
    () => Object.values(filters).some(Boolean),
    [filters],
  );

  const visibleSeries = useMemo(() => {
    if (!isAnyChecked) {
      return series;
    }

    return series.filter((item) => filters[item.key]);
  }, [filters, isAnyChecked, series]);

  const selectedHeading = useMemo(() => {
    if (!isAnyChecked) {
      return defaultHeading;
    }

    const selected = series.filter((item) => filters[item.key]).map((item) => item.label);
    return `Showing: ${selected.join(" + ")}`;
  }, [defaultHeading, filters, isAnyChecked, series]);

  const chartData = useMemo(() => {
    const allDates = series.flatMap((item) => (item.points || []).map((point) => point.date));
    const labels = buildContinuousDateRange(allDates).slice(-20);

    return {
      labels,
      datasets: visibleSeries.map((item) => {
        const valuesByDate = toSeriesMap(item.points);

        return {
          label: item.label,
          data: labels.map((label) => valuesByDate[label] || 0),
          borderColor: item.color,
          backgroundColor: item.background,
          pointBackgroundColor: item.color,
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: item.color,
          fill: true,
          tension: 0.35,
        };
      }),
    };
  }, [series, visibleSeries]);

  const setAll = (checked) => {
    const next = {};

    series.forEach((item) => {
      next[item.key] = checked;
    });

    setFilters(next);
  };

  if (loading) {
    return <div className="text-soft">{loadingMessage}</div>;
  }

  if (error) {
    return <div className="text-danger">{error}</div>;
  }

  if (series.length === 0 || chartData.labels.length === 0) {
    return <div className="text-soft">{emptyMessage}</div>;
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#364a63" }}>{selectedHeading}</div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {series.map((item) => (
            <TogglePill
              key={item.key}
              label={item.label}
              color={item.color}
              background={item.background}
              checked={Boolean(filters[item.key])}
              onChange={(checked) => setFilters((previous) => ({ ...previous, [item.key]: checked }))}
            />
          ))}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="button"
            onClick={() => setAll(true)}
            style={{
              border: "1px solid #e5e9f2",
              background: "#fff",
              padding: "9px 12px",
              borderRadius: "10px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 700,
              color: "#364a63",
            }}
          >
            Check All
          </button>
          <button
            type="button"
            onClick={() => setAll(false)}
            style={{
              border: "1px solid #e5e9f2",
              background: "#fff",
              padding: "9px 12px",
              borderRadius: "10px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 700,
              color: "#364a63",
            }}
          >
            Uncheck All
          </button>
        </div>
      </div>

      <div
        style={{
          height: `${chartHeight}px`,
          width: "100%",
          overflow: "hidden",
          borderRadius: "14px",
          border: "1px solid #e5e9f2",
          background: "#fff",
          padding: "10px",
          boxSizing: "border-box",
        }}
      >
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                backgroundColor: "rgba(17, 24, 39, 0.9)",
                titleColor: "#f9fafb",
                bodyColor: "#f9fafb",
                borderColor: "rgba(255, 255, 255, 0.1)",
                borderWidth: 1,
                callbacks: {
                  label: (context) => `${context.dataset.label}: ${Number(context.parsed.y || 0).toFixed(2)} MT`,
                },
              },
            },
            scales: {
              y: {
                ticks: {
                  color: "#9eaecf",
                  font: { size: 12, weight: "bold" },
                  padding: 6,
                },
                grid: { color: "rgba(82, 100, 132, 0.2)" },
                title: {
                  display: true,
                  text: yAxisTitle,
                  color: "#6b7280",
                  font: { size: 14, weight: "bold" },
                },
              },
              x: {
                ticks: {
                  color: "#9eaecf",
                  font: { size: 11, weight: "bold" },
                  padding: 6,
                  maxRotation: 45,
                  minRotation: 45,
                },
                grid: { color: "transparent" },
                title: {
                  display: true,
                  text: "Date",
                  color: "#6b7280",
                  font: { size: 14, weight: "bold" },
                },
              },
            },
            interaction: { intersect: false, mode: "index" },
          }}
        />
      </div>
    </div>
  );
};

export default StockMovementChart;
